import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { NOTE_CATEGORIES, NOTE_PRIORITIES, NOTE_SOURCE_TYPES } from "@alm/validation";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { OffsetPaginationFooter } from "@/components/app/lists/offset-pagination-footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { Plus } from "@/lib/icons";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import {
  filterPatch,
  optionalInt,
  readFilters,
  stringParams,
  KEEP_PLACE,
  optionalSearch,
} from "@/lib/search";
import { cn } from "@/lib/utils";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { NoteCard } from "./-components/note-card";
import {
  NO_NOTE_FILTERS,
  NoteFilters,
  noteFilterCount,
  type NoteFilterState,
} from "./-components/note-filters";
import { QuickNoteDialog } from "./-components/quick-note-dialog";
import { SearchField } from "@/components/app/patterns/search-field";

const FILTER_KEYS = ["entityType", "sourceType", "category", "priority", "authorId"] as const;
type NotesSearch = { q?: string; page?: number } & Partial<NoteFilterState>;

export const Route = createFileRoute("/_authenticated/_care/notes-center/")({
  validateSearch: (search: Record<string, unknown>): NotesSearch => ({
    q: optionalSearch(search.q),
    page: optionalInt(search.page, 2, 100000),
    ...stringParams(FILTER_KEYS, search),
  }),
  component: NotesCenterPage,
});

const LIMIT = 20;
const pick = <T extends string>(value: string) => (value === "all" ? undefined : (value as T));

/** Everything written across the buildings you can see, newest first. */
function NotesCenterPage() {
  const { facilityId } = useFacility();
  const canWrite = useCan("note:manage");
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<NotesSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(params.q, (q) => patch({ q, page: undefined }));
  const filters = readFilters(NO_NOTE_FILTERS, params);
  const page = params.page ?? 1;
  const setPage = (next: number) => patch({ page: next > 1 ? next : undefined });
  const [adding, setAdding] = useState(false);

  const authors = useQuery(orpc.care.notes.authors.queryOptions({}));
  const tags = useQuery(
    orpc.administration.catalogs.tags.list.queryOptions({ input: { facilityId } }),
  );
  const term = params.q ?? "";
  const feed = useQuery({
    ...orpc.care.notes.feed.queryOptions({
      input: {
        facilityId,
        search: term || undefined,
        entityType: pick<"resident" | "lead" | "referral_source">(filters.entityType),
        category: pick<(typeof NOTE_CATEGORIES)[number]>(filters.category),
        priority: pick<(typeof NOTE_PRIORITIES)[number]>(filters.priority),
        authorId: pick<string>(filters.authorId),
        sourceType: filters.sourceType as (typeof NOTE_SOURCE_TYPES)[number],
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || feed.isPlaceholderData;

  const rows = feed.data?.rows ?? [];
  const total = feed.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const start = (page - 1) * LIMIT;
  const filtering = Boolean(search.trim()) || noteFilterCount(filters) > 0;
  const tagColors = Object.fromEntries(
    (tags.data ?? [])
      .filter((tag) => tag.color)
      .map((tag) => [tag.name.trim().toLowerCase(), tag.color as string]),
  );
  const clear = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Notes center"
        count={feed.isSuccess ? total : undefined}
        meta={
          feed.isPending
            ? "Loading…"
            : feed.isError
              ? undefined
              : filtering
                ? "Matching your filters"
                : undefined
        }
        actions={
          canWrite ? (
            <Button onClick={() => setAdding(true)}>
              <Plus className="size-4" />
              Add note
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchField placeholder="Search notes" value={search} onChange={setSearch} />
        <NoteFilters
          filters={filters}
          authors={authors.data ?? []}
          onChange={(next) => patch({ ...filterPatch(NO_NOTE_FILTERS, next), page: undefined })}
        />
        {filtering ? (
          <Button variant="ghost" onClick={clear}>
            Clear filters
          </Button>
        ) : null}
      </div>

      {feed.isPending ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3, 4].map((index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : feed.isError ? (
        <ReadFailed
          message="Could not load the notes."
          error={feed.error}
          onRetry={() => void feed.refetch()}
          retrying={feed.isFetching}
        />
      ) : rows.length === 0 ? (
        <Card className="py-0">
          <EmptyState
            title={filtering ? "No notes match these filters" : "No notes yet"}
            description={
              filtering
                ? "Widen the search or clear a filter to see more."
                : "Notes and logged communications appear here as staff record them."
            }
            action={
              filtering ? (
                <Button variant="outline" onClick={clear}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <>
          <div
            aria-busy={searching || undefined}
            className={cn("flex flex-col gap-3 transition-opacity", searching && "opacity-60")}
          >
            {rows.map((row) => (
              <NoteCard
                key={`${row.sourceType}-${row.id}`}
                row={row}
                tagColors={tagColors}
                canWrite={canWrite}
              />
            ))}
          </div>
          {totalPages > 1 ? (
            <Card className="py-0">
              <OffsetPaginationFooter
                offset={start}
                pageSize={LIMIT}
                total={total}
                noun="items"
                onOffsetChange={(offset) => setPage(offset / LIMIT + 1)}
                onPageSizeChange={() => undefined}
              />
            </Card>
          ) : null}
        </>
      )}

      {adding ? <QuickNoteDialog facilityId={facilityId} onClose={() => setAdding(false)} /> : null}
    </div>
  );
}
